import { memo, useCallback, useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useTranslation } from 'react-i18next';
import { SCUITextFieldWithSelectBox } from '../../atomic/molecules';

/** Turkish mobile for BFF: `0090` + 10 digits starting with 5 (e.g. 00905317018769). */
export const normalizeTurkishPhoneForApi = (raw: string): string => {
    let digits = raw.trim().replace(/^\+/, '').replace(/\D/g, '');

    let prev = '';
    while (digits !== prev && digits.length > 0) {
        prev = digits;
        if (digits.startsWith('0090') && digits.length > 4) {
            digits = digits.slice(4);
        } else if (digits.startsWith('90') && digits.length > 2) {
            digits = digits.slice(2);
        } else if (digits.startsWith('0')) {
            digits = digits.slice(1);
        }
    }

    if (digits.startsWith('5') && digits.length > 10) {
        digits = digits.slice(0, 10);
    }

    return `0090${digits}`;
};

/** Up to 0090 + 10-digit mobile (e.g. 00905317018769). */
const sanitizePhoneInput = (value: string) => value.replace(/\D/g, '').slice(0, 14);

const sanitizeCustomerIdInput = (value: string) => value.replace(/\D/g, '').slice(0, 15);

export const STORE_CUSTOMER_SEARCH_TYPE = {
    phone: 1,
    customerId: 2,
} as const;

export type StoreCustomerSearchType =
    (typeof STORE_CUSTOMER_SEARCH_TYPE)[keyof typeof STORE_CUSTOMER_SEARCH_TYPE];

export type StoreCustomerSearchParams = {
    searchType: StoreCustomerSearchType;
    value: string;
};

type StoreCustomerPhoneSearchFormProps = {
    searchLoading: boolean;
    phoneLabel: string;
    customerIdLabel: string;
    searchAriaLabel: string;
    onSearch: (params: StoreCustomerSearchParams) => void;
};

export const StoreCustomerPhoneSearchForm = memo(function StoreCustomerPhoneSearchForm({
    searchLoading,
    phoneLabel,
    customerIdLabel,
    searchAriaLabel,
    onSearch,
}: StoreCustomerPhoneSearchFormProps) {
    const { t } = useTranslation();
    const [searchType, setSearchType] = useState<StoreCustomerSearchType>(
        STORE_CUSTOMER_SEARCH_TYPE.phone
    );
    const [searchValue, setSearchValue] = useState('');

    const selectList = useMemo(
        () => [
            { value: STORE_CUSTOMER_SEARCH_TYPE.phone, text: phoneLabel },
            { value: STORE_CUSTOMER_SEARCH_TYPE.customerId, text: customerIdLabel },
        ],
        [customerIdLabel, phoneLabel]
    );

    const submitSearch = useCallback(() => {
        const trimmed = searchValue.trim();
        if (!trimmed || searchLoading) {
            return;
        }
        onSearch({ searchType, value: trimmed });
    }, [onSearch, searchLoading, searchType, searchValue]);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                maxWidth: 520,
                mx: 'auto',
                mt: 2,
                background: (theme) =>
                    theme.palette.mode === 'dark'
                        ? theme.palette.background.paper
                        : 'linear-gradient(180deg, #f8fbff 0%, #ffffff 48%)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        boxShadow: '0 8px 24px rgba(25, 118, 210, 0.35)',
                    }}
                    aria-hidden
                >
                    <StorefrontIcon sx={{ fontSize: 44, color: '#fff' }} />
                </Box>

                <Typography variant="h6" fontWeight={700} textAlign="center" sx={{ mt: 0.5 }}>
                    {t('storeCustomerManagement.searchHeroTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 1 }}>
                    {t('storeCustomerManagement.searchHeroSubtitle')}
                </Typography>

                <Box sx={{ width: '100%' }}>
                    <SCUITextFieldWithSelectBox
                        placeholder={
                            searchType === STORE_CUSTOMER_SEARCH_TYPE.customerId
                                ? customerIdLabel
                                : phoneLabel
                        }
                        buttonText={searchAriaLabel}
                        selectList={selectList}
                        selectValue={searchType}
                        defaultText={searchValue}
                        onChangeSelect={(event: { target: { value: StoreCustomerSearchType } }) => {
                            setSearchType(Number(event.target.value) as StoreCustomerSearchType);
                            setSearchValue('');
                        }}
                        onChangeTextField={(event: { target: { value: string } }) => {
                            const raw = event.target.value;
                            const next =
                                searchType === STORE_CUSTOMER_SEARCH_TYPE.customerId
                                    ? sanitizeCustomerIdInput(raw)
                                    : sanitizePhoneInput(raw);
                            setSearchValue(next);
                        }}
                        onClickButton={submitSearch}
                        onKeyPressTextField={(e: { key: string; preventDefault: () => void }) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submitSearch();
                            }
                        }}
                    />
                </Box>
            </Box>
        </Paper>
    );
});
